class TootoCustomerVoice extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.tooto-customer-voice__viewport');
    this.track = this.querySelector('[data-tooto-customer-voice-track]');
    this.group = this.querySelector('[data-tooto-customer-voice-group]');
    if (!this.track || !this.group) return;

    this.speed = Number(this.dataset.speed || 22);
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.offset = 0;
    this.groupWidth = 0;
    this.lastTime = 0;
    this.isHovering = false;
    this.isPointerDown = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.hasAppliedMobileInitialOffset = false;
    this.activeCard = null;

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.group);
    if (this.viewport) this.resizeObserver.observe(this.viewport);
    this.measure();

    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerup', this.handlePointerUp);
    this.addEventListener('pointercancel', this.handlePointerUp);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('pointerover', this.handlePointerOver);
    this.addEventListener('pointerout', this.handlePointerOut);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.viewport?.addEventListener('scroll', this.handleViewportScroll, { passive: true });
    this.mobileQuery.addEventListener?.('change', this.measure);

    this.animationFrame = requestAnimationFrame(this.tick);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.animationFrame);
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerup', this.handlePointerUp);
    this.removeEventListener('pointercancel', this.handlePointerUp);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.removeEventListener('pointerover', this.handlePointerOver);
    this.removeEventListener('pointerout', this.handlePointerOut);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.viewport?.removeEventListener('scroll', this.handleViewportScroll);
    this.mobileQuery?.removeEventListener?.('change', this.measure);
  }

  measure = () => {
    this.groupWidth = this.group.getBoundingClientRect().width;

    if (this.mobileQuery.matches) {
      this.track.style.transform = '';
      this.updateActiveCard();
      return;
    }

    this.hasAppliedMobileInitialOffset = false;
    this.normalizeOffset();
    this.applyTransform();
  };

  getMobileInitialOffset() {
    const cards = Array.from(this.group.children);
    const targetCard = cards[Math.min(2, cards.length - 1)];
    if (!this.viewport || !(targetCard instanceof HTMLElement)) return this.offset;

    const viewportWidth = this.viewport.getBoundingClientRect().width;
    const targetCenter = targetCard.offsetLeft + targetCard.getBoundingClientRect().width / 2;

    return viewportWidth / 2 - targetCenter;
  }

  normalizeOffset() {
    if (!this.groupWidth) return;

    while (this.offset <= -this.groupWidth) this.offset += this.groupWidth;
    while (this.offset > 0) this.offset -= this.groupWidth;
  }

  applyTransform() {
    if (this.mobileQuery.matches) return;
    this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
  }

  tick = (time) => {
    if (!this.lastTime) this.lastTime = time;
    const delta = time - this.lastTime;
    this.lastTime = time;

    const isMobile = this.mobileQuery.matches;
    const isPaused = isMobile || this.prefersReducedMotion || this.isPointerDown || (this.pauseOnHover && this.isHovering);

    if (!isPaused && this.groupWidth) {
      this.offset -= (this.speed * delta) / 1000;
      this.normalizeOffset();
      this.applyTransform();
    }

    this.animationFrame = requestAnimationFrame(this.tick);
  };

  handlePointerDown = (event) => {
    if (this.mobileQuery.matches) return;
    if (event.button !== undefined && event.button !== 0) return;
    this.isPointerDown = true;
    this.pointerStartX = event.clientX;
    this.offsetStart = this.offset;
    this.classList.add('is-dragging');
    this.setPointerCapture(event.pointerId);
  };

  handlePointerMove = (event) => {
    if (this.mobileQuery.matches) return;
    if (!this.isPointerDown) return;
    event.preventDefault();
    this.offset = this.offsetStart + event.clientX - this.pointerStartX;
    this.normalizeOffset();
    this.applyTransform();
  };

  handlePointerUp = (event) => {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.classList.remove('is-dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
  };

  handlePointerLeave = (event) => {
    if (this.isPointerDown) this.handlePointerUp(event);
  };

  getCardFromEvent(event) {
    if (!(event.target instanceof Element)) return null;

    return event.target.closest('.tooto-customer-voice__card');
  }

  handlePointerOver = (event) => {
    if (!this.getCardFromEvent(event)) return;

    this.isHovering = true;
  };

  handlePointerOut = (event) => {
    const card = this.getCardFromEvent(event);
    if (!card) return;
    if (event.relatedTarget instanceof Node && card.contains(event.relatedTarget)) return;

    this.isHovering = false;
  };

  handleFocusIn = (event) => {
    if (!this.getCardFromEvent(event)) return;

    this.isHovering = true;
  };

  handleFocusOut = (event) => {
    const card = this.getCardFromEvent(event);
    if (!card) return;
    if (event.relatedTarget instanceof Node && card.contains(event.relatedTarget)) return;

    this.isHovering = false;
  };

  handleViewportScroll = () => {
    if (!this.mobileQuery.matches) return;
    window.requestAnimationFrame(() => this.updateActiveCard());
  };

  updateActiveCard() {
    if (!this.viewport || !this.group) return;

    const cards = Array.from(this.group.querySelectorAll('.tooto-customer-voice__card'));
    if (!cards.length) return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const viewportCenter = viewportRect.left + viewportRect.width / 2;
    let closestCard = cards[0];
    let closestDistance = Infinity;

    cards.forEach((card) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - viewportCenter);

      if (distance < closestDistance) {
        closestCard = card;
        closestDistance = distance;
      }
    });

    if (closestCard === this.activeCard) return;

    this.activeCard?.classList.remove('is-active');
    closestCard.classList.add('is-active');
    this.activeCard = closestCard;
  }
}

if (!customElements.get('tooto-customer-voice')) {
  customElements.define('tooto-customer-voice', TootoCustomerVoice);
}
